import {inject, Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, debounceTime, EMPTY, Observable, tap} from 'rxjs';
import {
  BtwOverzicht,
  BtwOverzichtFrequentie, BtwRekenService, CorporateTaxEntity,
  ErrorMessage,
  IntermediaryEntity,
  MenuInterface,
  ValueAddedTaxEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';

@Injectable({providedIn: 'root'})
export class BokkzBtwDataService implements OnDestroy {

  private apiService = inject(BtwRekenService);

  public valueAddedTaxEntity = new BehaviorSubject<ValueAddedTaxEntity>(
    this.getFromSessionStorage<ValueAddedTaxEntity>('valueAddedTaxEntity') || <ValueAddedTaxEntity>{},
  );

  public options = new BehaviorSubject<MenuInterface[]>([]);
  public errorResponse = new BehaviorSubject<ErrorMessage[]>([]);
  public aantalValidaties = new BehaviorSubject<number>(0);
  public periodeIndex = new BehaviorSubject<number>(0);

  public valueAddedTaxEntity$: Observable<CorporateTaxEntity> = this.valueAddedTaxEntity.asObservable();

  public patchValueAddedTaxEntity(updater: (entity: ValueAddedTaxEntity) => ValueAddedTaxEntity): void {
    const current = this.valueAddedTaxEntity.value;
    this.valueAddedTaxEntity.next(updater(current));
  }

  calculateLatestValueAddedTaxEntity(): Observable<BtwOverzicht> {
    const aangifte = this.valueAddedTaxEntity.value.overzicht;

    if (!aangifte) {
      return EMPTY;
    }

    return this.apiService.getValueAddedTaxCalculation(aangifte).pipe(
      tap(result => {
        const response = result as unknown as {overzicht?: BtwOverzicht};
        const nextOverzicht = response.overzicht ?? (result as BtwOverzicht);

        if (!nextOverzicht) {
          return;
        }

        const current = this.valueAddedTaxEntity.value;
        this.valueAddedTaxEntity.next({
          ...current,
          overzicht: nextOverzicht
        });
      })
    );
  }

  constructor() {
    this.applyDebounceTime();
  }

  private applyDebounceTime() {
    const debounceDelay = 500;
    // this.valueAddedTaxEntity = this.valueAddedTaxEntity.pipe(
    //   debounceTime(debounceDelay),
    //   tap((value) => this.saveToSessionStorage('valueAddedTaxEntity', value))) as BehaviorSubject<ValueAddedTaxEntity>;
    this.valueAddedTaxEntity
      .pipe(
        debounceTime(debounceDelay),
        tap((value) => this.saveToSessionStorage('valueAddedTaxEntity', value)),
      )
      .subscribe();

    this.errorResponse = this.errorResponse as BehaviorSubject<ErrorMessage[]>;
    this.aantalValidaties = this.aantalValidaties as BehaviorSubject<number>;
    this.aantalValidaties = this.aantalValidaties as BehaviorSubject<number>;
  }

  //Voor het ophalen uit de sessionstorage.
  private getFromSessionStorage<T>(key: string): T | null {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  }

  //Voor het opslaan in sessionstorage
  private saveToSessionStorage(key: string, value: any) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  updateValueAddedTaxEntity(valueAddedTaxEntity: ValueAddedTaxEntity, intermediaryEntity?: IntermediaryEntity) {
    if (valueAddedTaxEntity && valueAddedTaxEntity.overzicht) {
      if (valueAddedTaxEntity.overzicht?.icpAangiftes) {
        if (intermediaryEntity) {
          if (intermediaryEntity.beconId) {
            valueAddedTaxEntity.overzicht?.icpAangiftes?.forEach((aangifte) => {
              aangifte.beconnummer = intermediaryEntity.beconId!
            });
          }
          else {
            valueAddedTaxEntity.overzicht?.icpAangiftes?.forEach((aangifte) => {
              aangifte.beconnummer = undefined;
            });
          }
        }
      }
      this.createMenuOptions(valueAddedTaxEntity.overzicht);
      this.valueAddedTaxEntity.next(valueAddedTaxEntity);
    }
  }

  createMenuOptions(overzicht: BtwOverzicht) {
    const icpCount = overzicht.icpAangiftes?.length ?? 0;

    const options: MenuInterface[] = [
      {
        value: 0,
        id: '0',
        label: 'Algemeen',
        visible: true,
        subonderdelen: [
          {
            label: 'Belastingplichtige',
            pageMapping: 'Belastingplichtige',
            visible: true,
            value: 0,
          },
          {
            label: 'Fiscale eenheid',
            pageMapping: 'Fiscale eenheid',
            visible: false,
            value: 2,
          },
        ],
      },
      {
        value: 1,
        id: '1',
        label: 'BTW aangiften',
        visible: true,
        subonderdelen: [
          {
            label: 'Eerste kwartaal',
            pageMapping: 'Eerste kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 0,
          },
          {
            label: 'Tweede kwartaal',
            pageMapping: 'Tweede kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 1,
          },
          {
            label: 'Derde kwartaal',
            pageMapping: 'Derde kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 2,
          },
          {
            label: 'Vierde kwartaal',
            pageMapping: 'Vierde kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 3,
          },
          {
            label: 'Januari',
            pageMapping: 'Januari',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 0,
          },
          {
            label: 'Februari',
            pageMapping: 'Februari',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 1,
          },
          {
            label: 'Maart',
            pageMapping: 'Maart',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 2,
          },
          {
            label: 'April',
            pageMapping: 'April',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 3,
          },
          {
            label: 'Mei',
            pageMapping: 'Mei',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 4,
          },
          {
            label: 'Juni',
            pageMapping: 'Juni',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 5,
          },
          {
            label: 'Juli',
            pageMapping: 'Juli',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 6,
          },
          {
            label: 'Augustus',
            pageMapping: 'Augustus',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 7,
          },
          {
            label: 'September',
            pageMapping: 'September',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 8,
          },
          {
            label: 'Oktober',
            pageMapping: 'Oktober',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 9,
          },
          {
            label: 'November',
            pageMapping: 'November',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 10,
          },
          {
            label: 'December',
            pageMapping: 'December',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 11,
          },
          {
            label: 'Jaaraangifte',
            pageMapping: 'Jaaraangifte',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.JAAR,
            value: 0,
          },
        ],
      },
      {
        value: 2,
        id: '2',
        label: 'ICP opgaves',
        visible: true,
        subonderdelen: [
          {
            label: 'ICP opgave januari',
            pageMapping: 'ICP opgave januari',
            visible: icpCount >= 1,
            value: 0,
          },
          {
            label: 'ICP opgave februari',
            pageMapping: 'ICP opgave februari',
            visible: icpCount >= 2,
            value: 1,
          },
          {
            label: 'ICP opgave maart',
            pageMapping: 'ICP opgave maart',
            visible: icpCount >= 3,
            value: 2,
          },
          {
            label: 'ICP opgave april',
            pageMapping: 'ICP opgave april',
            visible: icpCount >= 4,
            value: 3,
          },
          {
            label: 'ICP opgave mei',
            pageMapping: 'ICP opgave mei',
            visible: icpCount >= 5,
            value: 4,
          },
          {
            label: 'ICP opgave juni',
            pageMapping: 'ICP opgave juni',
            visible: icpCount >= 6,
            value: 5,
          },
          {
            label: 'ICP opgave juli',
            pageMapping: 'ICP opgave juli',
            visible: icpCount >= 7,
            value: 6,
          },
          {
            label: 'ICP opgave augustus',
            pageMapping: 'ICP opgave augustus',
            visible: icpCount >= 8,
            value: 7,
          },
          {
            label: 'ICP opgave september',
            pageMapping: 'ICP opgave september',
            visible: icpCount >= 9,
            value: 8,
          },
          {
            label: 'ICP opgave oktober',
            pageMapping: 'ICP opgave oktober',
            visible: icpCount >= 10,
            value: 9,
          },
          {
            label: 'ICP opgave november',
            pageMapping: 'ICP opgave november',
            visible: icpCount >= 11,
            value: 10,
          },
          {
            label: 'ICP opgave december',
            pageMapping: 'ICP opgave december',
            visible: icpCount >= 12,
            value: 11,
          },
        ],
      },
      {
        value: 3,
        id: '3',
        label: 'Suppleties',
        visible: true,
        subonderdelen: [
          {
            label: 'Suppletie eerste kwartaal',
            pageMapping: 'Suppletie eerste kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 0,
          },
          {
            label: 'Suppletie tweede kwartaal',
            pageMapping: 'Suppletie tweede kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 1,
          },
          {
            label: 'Suppletie derde kwartaal',
            pageMapping: 'Suppletie derde kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 2,
          },
          {
            label: 'Suppletie vierde kwartaal',
            pageMapping: 'Suppletie vierde kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 3,
          },
          {
            label: 'Suppletie januari',
            pageMapping: 'Suppletie januari',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 0,
          },
          {
            label: 'Suppletie februari',
            pageMapping: 'Suppletie februari',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 1,
          },
          {
            label: 'Suppletie maart',
            pageMapping: 'Suppletie maart',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 2,
          },
          {
            label: 'Suppletie april',
            pageMapping: 'Suppletie april',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 3,
          },
          {
            label: 'Suppletie mei',
            pageMapping: 'Suppletie mei',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 4,
          },
          {
            label: 'Suppletie juni',
            pageMapping: 'Suppletie juni',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 5,
          },
          {
            label: 'Suppletie juli',
            pageMapping: 'Suppletie juli',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 6,
          },
          {
            label: 'Suppletie augustus',
            pageMapping: 'Suppletie augustus',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 7,
          },
          {
            label: 'Suppletie september',
            pageMapping: 'Suppletie september',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 8,
          },
          {
            label: 'Suppletie oktober',
            pageMapping: 'Suppletie oktober',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 9,
          },
          {
            label: 'Suppletie november',
            pageMapping: 'Suppletie november',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 10,
          },
          {
            label: 'Suppletie december',
            pageMapping: 'Suppletie december',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 11,
          },
          {
            label: 'Jaarsuppletie',
            pageMapping: 'Jaarsuppletie',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 4,
          },
          {
            label: 'Jaarsuppletie',
            pageMapping: 'Jaarsuppletie',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 12,
          },
            {label: 'Jaarsuppletie',
                pageMapping: 'Jaarsuppletie',
                visible: overzicht.frequentie === BtwOverzichtFrequentie.JAAR,
                value: 0,
            },
        ],
      },
      {
        value: 4,
        id: '4',
        label: 'Overzicht',
        visible: true,
        subonderdelen: [
          {
            label: 'Rondrekening',
            pageMapping: 'Rondrekening',
            visible: true,
            value: 0,
          },
        ],
      },
    ];
    this.options.next(options);
  }

  updateErrorResponse(errorResponse: ErrorMessage[]) {
    this.errorResponse.next(errorResponse);
  }

  updateAantalValidaties(aantalValidaties: number) {
    this.aantalValidaties.next(aantalValidaties);
  }

  updatePeriodeIndex(periodeIndex: number) {
    this.periodeIndex.next(periodeIndex);
  }

  ngOnDestroy(): void {
    this.valueAddedTaxEntity.complete();
    if (this.options) {
      this.options?.unsubscribe();
    }
    if (this.errorResponse) {
      this.errorResponse?.unsubscribe();
    }
    if (this.aantalValidaties) {
      this.aantalValidaties?.unsubscribe();
    }
    if (this.periodeIndex) {
      this.periodeIndex.unsubscribe();
    }
  }
}
